import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import CodeSnippetPanel from './CodeSnippetPanel';
import AssertionsPanel from './AssertionsPanel';
import {
  Send, Save, Plus, Trash2, Copy, Clock, CheckCircle,
  XCircle, Code, RefreshCw, Layers, Target, ChevronRight
} from 'lucide-react';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const METHOD_COLORS = { GET: '#30d986', POST: '#4f8ef7', PUT: '#f5a623', PATCH: '#9d6ef5', DELETE: '#f05c5c', HEAD: '#8892a4', OPTIONS: '#8892a4' };
const PARAM_TYPES = ['string', 'integer', 'number', 'boolean', 'array', 'object'];
const STATUS_CODES = [200, 201, 204, 301, 302, 400, 401, 403, 404, 409, 422, 429, 500, 502, 503];
const PARAM_INS = ['query', 'path', 'header', 'cookie'];

export default function EndpointEditor({ endpointId, projectId, projectBaseUrl, onSaved, environments }) {
  const [ep, setEp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('params');
  const [activeResponseTab, setActiveResponseTab] = useState('body');
  const [rightPanel, setRightPanel] = useState('response'); // response | snippets | assertions
  const [response, setResponse] = useState(null);
  const [rawRequestBody, setRawRequestBody] = useState('{}');
  const [activeEnvId, setActiveEnvId] = useState('');
  const [assertionResults, setAssertionResults] = useState(null);
  const [authType, setAuthType] = useState('none'); // none | bearer | basic | api_key
  const [authData, setAuthData] = useState({ token: '', username: '', password: '', key: '', value: '' });

  useEffect(() => { if (endpointId) loadEndpoint(); }, [endpointId]);
  useEffect(() => {
    if (environments?.length && !activeEnvId) setActiveEnvId(environments[0]?.id || '');
  }, [environments]);

  async function loadEndpoint() {
    setLoading(true);
    setResponse(null);
    setAssertionResults(null);
    try {
      const res = await api.get(`/endpoints/${endpointId}`);
      setEp(res.data);
      setRawRequestBody(res.data.body?.example_json || '{}');
      setAuthType(res.data.auth?.type || 'none');
      setAuthData(res.data.auth?.data || { token: '', username: '', password: '', key: '', value: '' });
    } catch { toast.error('Failed to load endpoint'); }
    finally { setLoading(false); }
  }

  const setField = (field, value) => setEp(e => ({ ...e, [field]: value }));

  const addParam = (param_in) => setEp(e => ({
    ...e,
    params: [...(e.params || []), { id: Date.now(), param_in, name: '', type: 'string', required: false, description: '', example_value: '' }]
  }));

  const updateParam = (idx, field, value) => setEp(e => {
    const params = [...(e.params || [])];
    params[idx] = { ...params[idx], [field]: value };
    return { ...e, params };
  });

  const removeParam = (idx) => setEp(e => ({ ...e, params: (e.params || []).filter((_, i) => i !== idx) }));

  const addResponse = () => setEp(e => ({
    ...e,
    responses: [...(e.responses || []), { id: Date.now(), status_code: 200, name: 'Success', content_type: 'application/json', example_json: '{"success": true}' }]
  }));

  const updateResponse = (idx, field, value) => setEp(e => {
    const responses = [...(e.responses || [])];
    responses[idx] = { ...responses[idx], [field]: value };
    return { ...e, responses };
  });

  const removeResponse = (idx) => setEp(e => ({ ...e, responses: (e.responses || []).filter((_, i) => i !== idx) }));

  async function save() {
    setSaving(true);
    try {
      await api.put(`/endpoints/${endpointId}`, {
        name: ep.name, method: ep.method, path: ep.path,
        description: ep.description, folder_id: ep.folder_id, status: ep.status,
        params: ep.params || [],
        auth: { type: authType, data: authData },
        body: ep.body ? { ...ep.body, example_json: rawRequestBody } : null,
        responses: ep.responses || [],
      });
      toast.success('Saved!');
      onSaved && onSaved();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  async function duplicate() {
    try {
      await api.post(`/endpoints/${endpointId}/duplicate`);
      toast.success('Endpoint duplicated!');
      onSaved && onSaved();
    } catch { toast.error('Failed to duplicate'); }
  }

  function buildUrl() {
    const activeEnv = environments?.find(e => e.id === activeEnvId);
    let base = projectBaseUrl || '';
    if (activeEnv?.variables) {
      activeEnv.variables.forEach(v => {
        if (v.enabled) base = base.replace(`{{${v.key_name}}}`, v.value);
      });
    }
    return `${base}${ep?.path || ''}`;
  }
  function resolveEnvVar(value) {
    const activeEnv = environments?.find(e => e.id === activeEnvId);
    if (activeEnv?.variables) {
      activeEnv.variables.forEach(v => {
        if (v.enabled) value = value.replace(new RegExp(`{{${v.key_name}}}`, 'g'), v.value);
      });
    }
    return value;
  }
  async function sendRequest() {
    setSending(true);
    setResponse(null);
    setAssertionResults(null);
    try {
      const url = buildUrl();
      const headers = {};
      const queryParams = {};
      (ep.params || []).forEach(p => {
        if (p.name && p.example_value) {
          if (p.param_in === 'header') headers[p.name] = resolveEnvVar(p.example_value);
          if (p.param_in === 'query') queryParams[p.name] = resolveEnvVar(p.example_value);
        }
      });

      // Add auth headers
      if (authType === 'bearer' && authData.token) {
        headers['Authorization'] = `Bearer ${resolveEnvVar(authData.token)}`;
      } else if (authType === 'basic' && authData.username && authData.password) {
        const creds = btoa(`${resolveEnvVar(authData.username)}:${resolveEnvVar(authData.password)}`);
        headers['Authorization'] = `Basic ${creds}`;
      } else if (authType === 'api_key' && authData.key && authData.value) {
        headers[authData.key] = resolveEnvVar(authData.value);
      }

      let body;
      if (['POST', 'PUT', 'PATCH'].includes(ep.method)) {
        try { body = JSON.parse(rawRequestBody); } catch { body = rawRequestBody; }
      }

      const res = await api.post('/send-request', { url, method: ep.method, headers, params: queryParams, body });
      setResponse(res.data);
      setRightPanel('response');

      await api.post('/test-runs', {
        endpoint_id: endpointId, project_id: projectId, method: ep.method, url,
        request_headers: headers, request_body: rawRequestBody,
        response_status: res.data.status, response_headers: res.data.headers,
        response_body: typeof res.data.data === 'object' ? JSON.stringify(res.data.data, null, 2) : res.data.data,
        response_time: res.data.response_time, success: res.data.status < 400
      });
    } catch (err) { toast.error('Request failed'); }
    finally { setSending(false); }
  }

  if (!endpointId) return (
    <div style={sty.emptyState}>
      <Code size={48} style={{ opacity: 0.12 }} />
      <p style={{ color: 'var(--text-muted)', marginTop: 14, fontSize: 14 }}>Select an endpoint to view or edit</p>
      <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>Or create a new endpoint using the button above</p>
    </div>
  );

  if (loading) return <div style={sty.emptyState}><RefreshCw size={24} style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }} /></div>;
  if (!ep) return null;

  const statusColor = !response ? null : response.status < 300 ? 'var(--accent-green)' : response.status < 500 ? 'var(--accent-orange)' : 'var(--accent-red)';

  return (
    <div style={sty.root}>
      {/* ── Top bar ── */}
      <div style={sty.topBar}>
        <input style={sty.nameInput} value={ep.name} onChange={e => setField('name', e.target.value)} placeholder="Endpoint name" />
        <div style={sty.topActions}>
          <select style={sty.statusSelect} value={ep.status || 'designing'} onChange={e => setField('status', e.target.value)}>
            {['designing','developing','testing','released','deprecated'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={duplicate} title="Duplicate endpoint">
            <Layers size={13} /> Duplicate
          </button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            <Save size={13} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* ── URL bar ── */}
      <div style={sty.urlBar}>
        <select style={{ ...sty.methodSelect, color: METHOD_COLORS[ep.method] }}
          value={ep.method} onChange={e => setField('method', e.target.value)}>
          {METHODS.map(m => <option key={m} value={m} style={{ color: METHOD_COLORS[m] }}>{m}</option>)}
        </select>
        <input style={sty.urlInput} value={ep.path} onChange={e => setField('path', e.target.value)}
          placeholder="/api/resource/{id}" className="mono" />
        <select style={sty.envSelect} value={activeEnvId} onChange={e => setActiveEnvId(e.target.value)}>
          <option value="">No Env</option>
          {(environments || []).map(env => <option key={env.id} value={env.id}>{env.name}</option>)}
        </select>
        <button style={{ ...sty.sendBtn, opacity: sending ? 0.8 : 1 }} onClick={sendRequest} disabled={sending}>
          {sending ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
          {sending ? 'Sending...' : 'Send'}
        </button>
      </div>

      {/* URL preview */}
      <div style={sty.urlPreview}>
        <ChevronRight size={11} style={{ color: 'var(--text-muted)' }} />
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--mono)' }}>{buildUrl()}</span>
      </div>

      {/* ── Main body ── */}
      <div style={sty.body}>
        {/* Left: request editor */}
        <div style={sty.editor}>
          <div className="tabs">
            {['params', 'auth', 'body', 'responses', 'description'].map(t => (
              <button key={t} className={`tab ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
                {t === 'params' && ep.params?.length ? ` (${ep.params.length})` : ''}
                {t === 'responses' && ep.responses?.length ? ` (${ep.responses.length})` : ''}
              </button>
            ))}
          </div>

          {/* Params */}
          {activeTab === 'params' && (
            <div style={sty.tabContent}>
              {PARAM_INS.map(pIn => {
                const pList = (ep.params || []).filter(p => p.param_in === pIn);
                return (
                  <div key={pIn} style={sty.paramSection}>
                    <div style={sty.paramHeader}>
                      <span style={sty.paramTitle}>{pIn.toUpperCase()} Params</span>
                      <button style={sty.addParamBtn} onClick={() => addParam(pIn)}><Plus size={11} /> Add</button>
                    </div>
                    {!pList.length && <div style={sty.noParams}>No {pIn} parameters</div>}
                    {pList.map(p => {
                      const realIdx = (ep.params || []).indexOf(p);
                      return (
                        <div key={realIdx} style={sty.paramRow}>
                          <input style={sty.paramCell} placeholder="name" value={p.name} onChange={e => updateParam(realIdx, 'name', e.target.value)} />
                          <select style={sty.paramTypeCell} value={p.type} onChange={e => updateParam(realIdx, 'type', e.target.value)}>
                            {PARAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <input style={sty.paramCell} placeholder="example" value={p.example_value} onChange={e => updateParam(realIdx, 'example_value', e.target.value)} />
                          <label style={sty.reqLabel}>
                            <input type="checkbox" checked={!!p.required} onChange={e => updateParam(realIdx, 'required', e.target.checked)} />
                            Req
                          </label>
                          <button style={sty.removeBtn} onClick={() => removeParam(realIdx)}><Trash2 size={12} /></button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Auth */}
          {activeTab === 'auth' && (
            <div style={sty.tabContent}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Auth Type</label>
                <select style={{ width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 6 }}
                  value={authType} onChange={e => setAuthType(e.target.value)}>
                  <option value="none">No Auth</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Auth</option>
                  <option value="api_key">API Key</option>
                </select>
              </div>
              {authType === 'bearer' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Token</label>
                  <input style={{ width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 6 }}
                    value={authData.token} onChange={e => setAuthData(d => ({ ...d, token: e.target.value }))}
                    placeholder="Enter token or use {{variable}}" />
                </div>
              )}
              {authType === 'basic' && (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Username</label>
                    <input style={{ width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 6 }}
                      value={authData.username} onChange={e => setAuthData(d => ({ ...d, username: e.target.value }))}
                      placeholder="Enter username or use {{variable}}" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Password</label>
                    <input type="password" style={{ width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 6 }}
                      value={authData.password} onChange={e => setAuthData(d => ({ ...d, password: e.target.value }))}
                      placeholder="Enter password or use {{variable}}" />
                  </div>
                </div>
              )}
              {authType === 'api_key' && (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Key</label>
                    <input style={{ width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 6 }}
                      value={authData.key} onChange={e => setAuthData(d => ({ ...d, key: e.target.value }))}
                      placeholder="Header name, e.g., X-API-Key" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Value</label>
                    <input style={{ width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 6 }}
                      value={authData.value} onChange={e => setAuthData(d => ({ ...d, value: e.target.value }))}
                      placeholder="Enter value or use {{variable}}" />
                  </div>
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
                Use &#123;&#123;variable&#125;&#125; to reference environment variables from the selected environment.
              </div>
            </div>
          )}

          {/* Body */}
          {activeTab === 'body' && (
            <div style={sty.tabContent}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                <select style={{ width: 260 }} value={ep.body?.content_type || 'application/json'}
                  onChange={e => setEp(ep => ({ ...ep, body: { ...(ep.body || {}), content_type: e.target.value } }))}>
                  <option value="application/json">application/json</option>
                  <option value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</option>
                  <option value="multipart/form-data">multipart/form-data</option>
                  <option value="text/plain">text/plain</option>
                </select>
                <button className="btn btn-ghost btn-sm" onClick={() => {
                  try { setRawRequestBody(JSON.stringify(JSON.parse(rawRequestBody), null, 2)); }
                  catch { toast.error('Invalid JSON'); }
                }}>Format</button>
                {!ep.body && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setEp(e => ({ ...e, body: { content_type: 'application/json', example_json: rawRequestBody } }))}>
                    Enable Body
                  </button>
                )}
              </div>
              <textarea style={sty.codeArea} value={rawRequestBody}
                onChange={e => setRawRequestBody(e.target.value)} rows={16} className="mono"
                placeholder={'{\n  "key": "value"\n}'} />
            </div>
          )}

          {/* Responses */}
          {activeTab === 'responses' && (
            <div style={sty.tabContent}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <button className="btn btn-ghost btn-sm" onClick={addResponse}><Plus size={13} /> Add Response</button>
              </div>
              {(ep.responses || []).map((r, idx) => (
                <div key={idx} style={sty.responseBlock}>
                  <div style={sty.responseBlockHeader}>
                    <select style={{ ...sty.statusCodeSelect, color: r.status_code < 300 ? 'var(--accent-green)' : r.status_code < 500 ? 'var(--accent-orange)' : 'var(--accent-red)' }}
                      value={r.status_code} onChange={e => updateResponse(idx, 'status_code', parseInt(e.target.value))}>
                      {STATUS_CODES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input placeholder="Response name" value={r.name} onChange={e => updateResponse(idx, 'name', e.target.value)} style={{ flex: 1 }} />
                    <button style={sty.removeBtn} onClick={() => removeResponse(idx)}><Trash2 size={12} /></button>
                  </div>
                  <textarea style={{ ...sty.codeArea, minHeight: 70 }} className="mono"
                    value={r.example_json} onChange={e => updateResponse(idx, 'example_json', e.target.value)} />
                </div>
              ))}
            </div>
          )}

          {/* Description */}
          {activeTab === 'description' && (
            <div style={sty.tabContent}>
              <textarea style={{ ...sty.codeArea, minHeight: 200, fontFamily: 'var(--sans)', color: 'var(--text-primary)' }}
                value={ep.description || ''} onChange={e => setField('description', e.target.value)}
                placeholder="Describe what this endpoint does, expected usage, edge cases..." />
            </div>
          )}
        </div>

        {/* Right: response / snippets / assertions */}
        <div style={sty.rightPanel}>
          {/* Right panel tabs */}
          <div style={sty.rightTabs}>
            <button style={{ ...sty.rightTab, ...(rightPanel === 'response' ? sty.rightTabActive : {}) }}
              onClick={() => setRightPanel('response')}>
              Response
              {response && <span style={{ ...sty.statusPill, background: `${statusColor}22`, color: statusColor }}>
                {response.status}
              </span>}
            </button>
            <button style={{ ...sty.rightTab, ...(rightPanel === 'snippets' ? sty.rightTabActive : {}) }}
              onClick={() => setRightPanel('snippets')}>
              <Code size={12} /> Code
            </button>
            <button style={{ ...sty.rightTab, ...(rightPanel === 'assertions' ? sty.rightTabActive : {}) }}
              onClick={() => setRightPanel('assertions')}>
              <Target size={12} /> Tests
              {assertionResults && (
                <span style={{ ...sty.statusPill, background: assertionResults.failed === 0 ? 'rgba(48,217,134,0.2)' : 'rgba(240,92,92,0.2)', color: assertionResults.failed === 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  {assertionResults.passed}/{assertionResults.total}
                </span>
              )}
            </button>
          </div>

          {/* Response panel */}
          {rightPanel === 'response' && (
            <div style={sty.responseContent}>
              {!response && !sending && (
                <div style={sty.noResponse}>
                  <Send size={32} style={{ opacity: 0.12 }} />
                  <p style={{ color: 'var(--text-muted)', marginTop: 12, textAlign: 'center' }}>
                    Hit <strong>Send</strong> to make a request<br />
                    <span style={{ fontSize: 11, marginTop: 4, display: 'block' }}>Results appear here</span>
                  </p>
                </div>
              )}
              {sending && (
                <div style={sty.noResponse}>
                  <RefreshCw size={28} style={{ animation: 'spin 0.8s linear infinite', opacity: 0.3 }} />
                  <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Sending request...</p>
                </div>
              )}
              {response && !sending && (
                <>
                  {/* Status bar */}
                  <div style={sty.responseStatusBar}>
                    {response.error ? (
                      <span style={{ color: 'var(--accent-red)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <XCircle size={14} /> {response.error}
                      </span>
                    ) : (
                      <>
                        <span style={{ ...sty.statusBadge, color: statusColor, background: `${statusColor}18` }}>
                          {response.status} {response.statusText}
                        </span>
                        <span style={sty.timeBadge}><Clock size={11} /> {response.response_time}ms</span>
                        <button style={sty.copySmall} onClick={() => { navigator.clipboard.writeText(typeof response.data === 'object' ? JSON.stringify(response.data, null, 2) : String(response.data)); toast.success('Copied!'); }}>
                          <Copy size={11} /> Copy
                        </button>
                      </>
                    )}
                  </div>

                  {!response.error && (
                    <>
                      {/* Response sub-tabs */}
                      <div style={sty.respSubTabs}>
                        {['body', 'headers'].map(t => (
                          <button key={t} style={{ ...sty.respSubTab, ...(activeResponseTab === t ? sty.respSubTabActive : {}) }}
                            onClick={() => setActiveResponseTab(t)}>
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                          </button>
                        ))}
                      </div>

                      {activeResponseTab === 'body' && (
                        <pre style={sty.responseBody} className="mono">
                          {typeof response.data === 'object'
                            ? JSON.stringify(response.data, null, 2)
                            : String(response.data || '')}
                        </pre>
                      )}

                      {activeResponseTab === 'headers' && (
                        <div style={sty.headersList}>
                          {Object.entries(response.headers || {}).map(([k, v]) => (
                            <div key={k} style={sty.headerItem}>
                              <span style={sty.headerKey}>{k}</span>
                              <span style={sty.headerVal}>{v}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Code snippets */}
          {rightPanel === 'snippets' && (
            <CodeSnippetPanel endpointId={endpointId} environments={environments} />
          )}

          {/* Assertions */}
          {rightPanel === 'assertions' && (
            <AssertionsPanel endpointId={endpointId} lastResponse={response} />
          )}
        </div>
      </div>
    </div>
  );
}

const sty = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' },
  topBar: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', flexShrink: 0 },
  nameInput: { flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, padding: 0, fontFamily: 'var(--sans)' },
  topActions: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  statusSelect: { background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, padding: '4px 8px', cursor: 'pointer' },
  urlBar: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', flexShrink: 0 },
  methodSelect: { background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 6, fontWeight: 700, fontSize: 12, padding: '6px 8px', cursor: 'pointer', fontFamily: 'var(--mono)', width: 96 },
  urlInput: { flex: 1, background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, padding: '6px 10px' },
  envSelect: { background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, padding: '6px 8px', cursor: 'pointer', width: 120 },
  sendBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)', flexShrink: 0, transition: 'all 0.15s' },
  urlPreview: { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-primary)', flexShrink: 0 },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  editor: { flex: '0 0 55%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid var(--border)', padding: '0 16px' },
  tabContent: { flex: 1, overflow: 'auto', paddingBottom: 20 },
  paramSection: { marginBottom: 20 },
  paramHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  paramTitle: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  addParamBtn: { display: 'flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--sans)' },
  noParams: { fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', fontStyle: 'italic' },
  paramRow: { display: 'grid', gridTemplateColumns: '1fr 90px 1fr auto auto', gap: 5, alignItems: 'center', marginBottom: 5 },
  paramCell: { padding: '5px 7px', fontSize: 12 },
  paramTypeCell: { padding: '5px 4px', fontSize: 11 },
  reqLabel: { display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' },
  removeBtn: { background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', padding: 3, display: 'flex' },
  codeArea: { width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 6, color: '#a8d8a8', fontSize: 12, padding: '10px', resize: 'vertical', minHeight: 180, lineHeight: 1.6, fontFamily: 'var(--mono)' },
  responseBlock: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: 10, marginBottom: 8 },
  responseBlockHeader: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 },
  statusCodeSelect: { background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 5, fontWeight: 700, fontFamily: 'var(--mono)', fontSize: 12, padding: '4px 6px' },
  rightPanel: { flex: '0 0 45%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-primary)' },
  rightTabs: { display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', flexShrink: 0 },
  rightTab: { display: 'flex', alignItems: 'center', gap: 5, padding: '9px 16px', border: 'none', background: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--sans)', fontWeight: 500, borderBottom: '2px solid transparent', marginBottom: -1, transition: 'all 0.12s' },
  rightTabActive: { color: 'var(--accent)', borderBottomColor: 'var(--accent)', background: 'none' },
  statusPill: { padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)' },
  responseContent: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  noResponse: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 },
  responseStatusBar: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  statusBadge: { padding: '3px 8px', borderRadius: 5, fontSize: 12, fontWeight: 700, fontFamily: 'var(--mono)' },
  timeBadge: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)' },
  copySmall: { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--sans)', marginLeft: 'auto' },
  respSubTabs: { display: 'flex', gap: 0, padding: '0 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  respSubTab: { padding: '6px 12px', border: 'none', background: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--sans)', borderBottom: '2px solid transparent', marginBottom: -1 },
  respSubTabActive: { color: 'var(--accent)', borderBottomColor: 'var(--accent)' },
  responseBody: { flex: 1, overflow: 'auto', padding: '14px', fontSize: 11, color: '#a8d8a8', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7, margin: 0, fontFamily: 'var(--mono)' },
  headersList: { flex: 1, overflow: 'auto', padding: '10px 14px' },
  headerItem: { display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)', alignItems: 'start' },
  headerKey: { fontSize: 11, color: 'var(--accent)', fontFamily: 'var(--mono)', wordBreak: 'break-all' },
  headerVal: { fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--mono)', wordBreak: 'break-all' },
};
